import { message } from "antd";
import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

const UserNotificationContext = createContext();

// The notify functions are memoized so consumers can safely list them in
// hook dependency arrays without re-triggering effects on every render.
export const UserNotificationProvider = ({ children }) => {
  const { t } = useTranslation();

  // Memoized so consumers can safely put these in hook dependency lists
  // (useRobotCommands, useRobotControlManager, ...) without re-running effects
  // on every render of the provider.
  const notifyError = useCallback((msg, translationKey = null) => {
    const displayMessage = translationKey ? t(translationKey) : msg;
    // console.log("Error notification:", displayMessage);
    message.error({
      content: displayMessage,
      // duration: 0, // Set duration to 0 to keep the message open
      style: {
        marginTop: "20vh",
      },
    });
  }, [t]);

  const notifySuccess = useCallback((msg, translationKey = null) => {
    const displayMessage = translationKey ? t(translationKey) : msg;
    message.success(displayMessage);
  }, [t]);

  const notifyInfo = useCallback((msg, translationKey = null) => {
    const displayMessage = translationKey ? t(translationKey) : msg;
    message.info(displayMessage);
  }, [t]);

  const notifyWarning = useCallback((msg, translationKey = null) => {
    const displayMessage = translationKey ? t(translationKey) : msg;
    message.warning(displayMessage);
  }, [t]);

  const value = useMemo(() => ({
    notifyError,
    notifySuccess,
    notifyInfo,
    notifyWarning,
    // Convenience methods with built-in translations
    notifyLoginSuccess: () => notifySuccess("", "notifications.loginSuccess"),
    notifyLoginError: () => notifyError("", "notifications.loginError"),
    notifyLogoutSuccess: () => notifySuccess("", "notifications.logoutSuccess"),
    notifySignupSuccess: () => notifySuccess("", "notifications.signupSuccess"),
    notifySignupError: () => notifyError("", "notifications.signupError"),
    notifySaveSuccess: () => notifySuccess("", "notifications.saveSuccess"),
    notifySaveError: () => notifyError("", "notifications.saveError"),
    notifyDeleteSuccess: () => notifySuccess("", "notifications.deleteSuccess"),
    notifyDeleteError: () => notifyError("", "notifications.deleteError"),
    notifyUpdateSuccess: () => notifySuccess("", "notifications.updateSuccess"),
    notifyUpdateError: () => notifyError("", "notifications.updateError"),
    notifyNetworkError: () => notifyError("", "notifications.networkError"),
    notifyGenericError: () => notifyError("", "notifications.genericError"),
  }), [notifyError, notifySuccess, notifyInfo, notifyWarning]);

  // Expose context
  return (
    <UserNotificationContext.Provider value={value}>
      {children}
    </UserNotificationContext.Provider>
  );
};

export const useNotification = () => useContext(UserNotificationContext);
